import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trophy, Calendar, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/db';
import { logger } from '../services/logger';
import { usePromoConfig } from '../hooks/usePromoConfig';
import { SEO } from '../components/SEO';

interface CompetitionTerms {
  competition_id: string;
  promo_name: string;
  terms_content: string | null;
  terms_version: number;
  terms_updated_at: string | null;
}

export function CompetitionTerms() {
  const [terms, setTerms] = useState<CompetitionTerms | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { promoConfig } = usePromoConfig();

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase.rpc('get_competition_terms', {
        p_competition_id: null
      });

      if (fetchError) throw fetchError;

      if (data && data.length > 0) {
        setTerms(data[0] as CompetitionTerms);
      }
    } catch (err) {
      logger.error('Error fetching competition terms:', err);
      setError('Failed to load competition terms');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-wayja-teal" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <SEO
        title="Competition Terms & Conditions"
        description="View the terms and conditions for the current Wayja trading competition."
        url="/competition-terms"
      />
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/" className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 p-2 rounded-full">
              <Trophy className="w-6 h-6 text-purple-600" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold">Competition Terms & Conditions</h1>
          </div>
        </div>

        {/* Competition Info Card */}
        {promoConfig?.is_active && (
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-lg p-6 mb-6 shadow-lg">
            <h2 className="text-xl font-bold mb-4">{promoConfig.promo_name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 opacity-80" />
                <div>
                  <p className="text-xs opacity-80">Start Date</p>
                  <p className="font-medium">{formatDate(promoConfig.start_date)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 opacity-80" />
                <div>
                  <p className="text-xs opacity-80">End Date</p>
                  <p className="font-medium">{formatDate(promoConfig.end_date)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 opacity-80" />
                <div>
                  <p className="text-xs opacity-80">Starting Balance</p>
                  <p className="font-medium">R{promoConfig.starting_balance?.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* No Active Competition */}
        {!promoConfig?.is_active && !terms && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No Active Competition</h2>
            <p className="text-gray-500">
              There is currently no active trading competition. Check back later for updates!
            </p>
            <Link
              to="/"
              className="inline-block mt-4 px-4 py-2 bg-wayja-teal text-white rounded-lg hover:bg-wayja-dark-teal transition-colors"
            >
              Back to Markets
            </Link>
          </div>
        )}

        {/* Terms Content */}
        {terms?.terms_content ? (
          <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
            <div className="prose prose-gray max-w-none">
              {/* Render terms content - split by lines and format */}
              {terms.terms_content.split('\n').map((line, index) => {
                // Handle headers
                if (line.startsWith('# ')) {
                  return <h1 key={index} className="text-2xl font-bold text-gray-900 mt-6 mb-4">{line.slice(2)}</h1>;
                }
                if (line.startsWith('## ')) {
                  return <h2 key={index} className="text-xl font-semibold text-gray-800 mt-5 mb-3">{line.slice(3)}</h2>;
                }
                if (line.startsWith('### ')) {
                  return <h3 key={index} className="text-lg font-medium text-gray-700 mt-4 mb-2">{line.slice(4)}</h3>;
                }
                // Handle numbered lists
                if (/^\d+\.\s/.test(line)) {
                  return <p key={index} className="pl-4 mb-2 text-gray-600">{line}</p>;
                }
                // Handle bullet points
                if (line.startsWith('- ')) {
                  return <p key={index} className="pl-4 mb-2 text-gray-600">• {line.slice(2)}</p>;
                }
                // Empty lines
                if (line.trim() === '') {
                  return <br key={index} />;
                }
                // Regular paragraphs
                return <p key={index} className="mb-2 text-gray-600">{line}</p>;
              })}
            </div>
            
            {/* Version Info */}
            {terms.terms_version && terms.terms_updated_at && (
              <div className="mt-8 pt-4 border-t border-gray-200 text-sm text-gray-500">
                <p>Version {terms.terms_version} • Last updated: {formatDate(terms.terms_updated_at)}</p>
              </div>
            )}
          </div>
        ) : promoConfig?.is_active && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Terms Coming Soon</h2>
            <p className="text-gray-500">
              The competition terms and conditions are being prepared. Please check back later.
            </p>
          </div>
        )}

        {/* Back Link */}
        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-wayja-teal hover:text-wayja-dark-teal font-medium"
          >
            ← Back to Markets
          </Link>
        </div>
      </div>
    </div>
  );
}

export default CompetitionTerms;

